import React, { Component } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { Redirect, withRouter } from "react-router-dom";
import styled from "styled-components";
import { isTrackCodeInternalUser } from "common/trackcodeInternal";
import { resolveActiveLogisticCompanyId } from "features/app/components/logisticCompanySwitcherUtils";
import {
  findProjectByPath,
  isProjectAllowedForCompany
} from "features/app/components/platformSidebarProjects";
import CollaborationHeader, {
  navigateCollaborationAfterTenantSwitch
} from "features/collaboration/components/CollaborationHeader";
import {
  loadWencoPageHtml,
  WENCO_PAGE
} from "features/projects/wencoPageRegistry";

const INTERNAL_HTML_PAGES = {
  "/internal/wenco-tourenoptimierung": {
    title: "Wenco Tourenoptimierung",
    pageKey: WENCO_PAGE.INTERN_TOUREN_DASHBOARD,
    requireInternal: true
  }
};

const mapStateToProps = ({ auth: { user, logisticCompanyId } }) => ({
  user,
  logisticCompanyId
});

export class ProjectHtmlPage extends Component {
  static propTypes = {
    user: PropTypes.object,
    logisticCompanyId: PropTypes.number,
    history: PropTypes.shape({
      push: PropTypes.func.isRequired
    }).isRequired,
    location: PropTypes.shape({
      pathname: PropTypes.string.isRequired
    }).isRequired
  };

  static defaultProps = {
    user: null,
    logisticCompanyId: 0
  };

  state = {
    frameSrc: null,
    loadError: false,
    denied: false,
    frameTitle: "Projekt",
    headerContext: null,
    showHeader: false
  };

  blobUrl = null;
  loadGeneration = 0;
  cancelLoad = null;

  componentDidMount() {
    this.resolveAndLoad();
  }

  componentDidUpdate(prevProps) {
    const { location, user, logisticCompanyId } = this.props;
    if (
      prevProps.location.pathname !== location.pathname ||
      prevProps.user !== user ||
      prevProps.logisticCompanyId !== logisticCompanyId
    ) {
      this.resolveAndLoad();
    }
  }

  componentWillUnmount() {
    if (typeof this.cancelLoad === "function") {
      this.cancelLoad();
    }
    this.revokeBlob();
  }

  revokeBlob = () => {
    if (this.blobUrl) {
      URL.revokeObjectURL(this.blobUrl);
      this.blobUrl = null;
    }
  };

  resolvePage = () => {
    const { user, logisticCompanyId, location } = this.props;
    const activeCompanyId = resolveActiveLogisticCompanyId(
      logisticCompanyId,
      user
    );
    const internalPage = INTERNAL_HTML_PAGES[location.pathname];
    const project = findProjectByPath(location.pathname);

    if (internalPage?.requireInternal && !isTrackCodeInternalUser(user)) {
      return { denied: true };
    }

    if (project && !isProjectAllowedForCompany(project, activeCompanyId)) {
      return { denied: true };
    }

    const pageKey = internalPage?.pageKey || project?.pageKey;
    const frameTitle = internalPage?.title || project?.title || "Projekt";
    const headerContext = project?.headerContext || null;
    const showHeader = Boolean(project);

    if (!pageKey) {
      return { denied: true };
    }

    return { pageKey, frameTitle, headerContext, showHeader };
  };

  resolveAndLoad = () => {
    if (typeof this.cancelLoad === "function") {
      this.cancelLoad();
    }

    const resolved = this.resolvePage();
    if (resolved.denied) {
      this.revokeBlob();
      this.setState({
        denied: true,
        frameSrc: null,
        loadError: false,
        frameTitle: "Projekt",
        headerContext: null,
        showHeader: false
      });
      return;
    }

    const { pageKey, frameTitle, headerContext, showHeader } = resolved;
    this.setState({
      denied: false,
      frameTitle,
      headerContext,
      showHeader,
      frameSrc: null,
      loadError: false
    });

    let cancelled = false;
    this.loadGeneration += 1;
    const generation = this.loadGeneration;
    this.cancelLoad = () => {
      cancelled = true;
    };

    loadWencoPageHtml(pageKey)
      .then(html => {
        if (cancelled || generation !== this.loadGeneration) {
          return;
        }
        this.revokeBlob();
        this.blobUrl = URL.createObjectURL(
          new Blob([html], { type: "text/html;charset=utf-8" })
        );
        this.setState({ frameSrc: this.blobUrl, loadError: false });
      })
      .catch(() => {
        if (cancelled || generation !== this.loadGeneration) {
          return;
        }
        this.revokeBlob();
        this.setState({ frameSrc: null, loadError: true });
      });
  };

  renderHeader = () => {
    const { showHeader, headerContext } = this.state;
    if (!showHeader) {
      return null;
    }

    return (
      <CollaborationHeader
        contextLabel={headerContext}
        onAfterSwitch={navigateCollaborationAfterTenantSwitch}
      />
    );
  };

  render() {
    const { denied, frameSrc, loadError, frameTitle } = this.state;

    if (denied) {
      return <Redirect to="/collaboration" />;
    }

    if (loadError) {
      return (
        <Page>
          {this.renderHeader()}
          <FrameWrap>
            <Status>Seite konnte nicht geladen werden.</Status>
          </FrameWrap>
        </Page>
      );
    }

    if (!frameSrc) {
      return (
        <Page>
          {this.renderHeader()}
          <FrameWrap>
            <Status>Laden…</Status>
          </FrameWrap>
        </Page>
      );
    }

    return (
      <Page>
        {this.renderHeader()}
        <FrameWrap>
          <Frame title={frameTitle} src={frameSrc} />
        </FrameWrap>
      </Page>
    );
  }
}

const Page = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  width: 100%;
  height: 100%;
  background: #f7f6f2;
`;

const FrameWrap = styled.div`
  flex: 1;
  min-height: 0;
  width: 100%;
  background: #f7f6f2;
`;

const Frame = styled.iframe`
  display: block;
  width: 100%;
  height: 100%;
  border: 0;
`;

const Status = styled.div`
  padding: 24px;
  color: #5f5e5a;
  font-size: 13px;
`;

export default withRouter(connect(mapStateToProps)(ProjectHtmlPage));
